import { enhancedApi } from '@store/generated/appointments';
import { AppointmentQueryParams } from '../types';
import type {
  GetV2ProviderScheduleEventsApiArg,
} from '@store/generated/appointments';
import { dispatchClearActiveEncounter } from '@utils/activeEncounterEvents';

/**
 * Feature-specific tag types for appointments cache management
 */
const APPOINTMENT_TAG_TYPES = [
  'Appointment',
  'AppointmentsList',
  'NextAppointment',
  'AppointmentSeries',
  'BlockedTime',
  'BlockedTimeList',
] as const;

const SCHEDULE_EVENTS_LIST_TAGS = [
  { type: 'AppointmentsList' as const, id: 'LIST' },
  { type: 'BlockedTimeList' as const, id: 'LIST' },
];

// Type for Redux state with API queries (for optimistic updates)
interface RootStateWithQueries {
  api: {
    queries: Record<
      string,
      {
        originalArgs: any;
        data?: CachedAppointmentsData;
      }
    >;
  };
}

type CachedAppointment = {
  appointment_id: string;
  encounter?: { encounter_id?: string | null } | null;
  series?: { series_id?: string | null } | null;
};

type CachedAppointmentsData = {
  items?: CachedAppointment[];
  appointments?: CachedAppointment[];
};

const getLegacyAppointmentListQueryKeys = (
  queries: RootStateWithQueries['api']['queries'],
) => Object.keys(queries).filter(key => key.startsWith('getV2ProviderAppointments('));

const getScheduleEventsQueryKeys = (
  queries: RootStateWithQueries['api']['queries'],
) => Object.keys(queries).filter(key => key.startsWith('getV2ProviderScheduleEvents('));

const getCachedAppointments = (data?: CachedAppointmentsData): CachedAppointment[] =>
  data?.items ?? data?.appointments ?? [];

/**
 * Appointments API - extends the generated OpenAPI client
 * This ensures we're using generated types and endpoints
 * while maintaining feature-specific customization
 */
export const appointmentsApi = enhancedApi
  .enhanceEndpoints({
    addTagTypes: [
      ...APPOINTMENT_TAG_TYPES,
      // Cross-feature: invalidate encounter cache when appointment is updated
      // (encounter responses nest appointment data including notes)
      'Encounter',
    ],
    endpoints: {
      // Enhance the list appointments endpoint with proper caching
      getV2ProviderAppointments: {
        providesTags: result =>
          result
            ? [
                ...result.items.map(({ appointment_id }) => ({
                  type: 'Appointment' as const,
                  id: appointment_id,
                })),
                { type: 'AppointmentsList' as const, id: 'LIST' },
              ]
            : [{ type: 'AppointmentsList', id: 'LIST' }],
      },

      // Enhance the unified calendar events endpoint with appointments and blocked-time tags.
      getV2ProviderScheduleEvents: {
        providesTags: result =>
          result
            ? [
                ...(result.appointments ?? []).map(({ appointment_id }) => ({
                  type: 'Appointment' as const,
                  id: appointment_id,
                })),
                ...(result.blocked_times ?? []).map(({ availability_rule_id }) => ({
                  type: 'BlockedTime' as const,
                  id: availability_rule_id,
                })),
                ...SCHEDULE_EVENTS_LIST_TAGS,
              ]
            : SCHEDULE_EVENTS_LIST_TAGS,
      },

      // Enhance the next appointment endpoint
      getV2ProviderAppointmentsNext: {
        providesTags: [{ type: 'NextAppointment', id: 'NEXT' }],
      },

      // Enhance the get appointment by ID endpoint
      getV2ProviderAppointmentsByAppointmentId: {
        providesTags: (result, error, { appointmentId }) => {
          const tags: (
            | { type: 'Appointment'; id: string }
            | { type: 'AppointmentSeries'; id: string }
          )[] = [{ type: 'Appointment', id: appointmentId }];
          // Also tag by series so a series-level edit (PATCH /series/{id}, which
          // invalidates AppointmentSeries) refreshes this open by-id query — e.g.
          // the detail drawer reflects a recurrence change without a remount.
          if (result?.series?.series_id) {
            tags.push({ type: 'AppointmentSeries', id: result.series.series_id });
          }
          return tags;
        },
      },

      // Enhance appointment creation with cache invalidation
      postV2ProviderAppointments: {
        invalidatesTags: [
          { type: 'AppointmentsList', id: 'LIST' },
          { type: 'NextAppointment', id: 'NEXT' },
        ],
        // Add optimistic updates for appointment creation
        onQueryStarted: async ({ appointmentCreate }, { dispatch, queryFulfilled }) => {
          try {
            const { data: newAppointment } = await queryFulfilled;

            // Optimistically update any relevant list queries
            dispatch(
              appointmentsApi.util.invalidateTags([{ type: 'AppointmentsList', id: 'LIST' }]),
            );
          } catch (error) {
            // Handle creation error
            console.error('Failed to create appointment:', error);
          }
        },
      },

      // Enhance appointment updates with optimistic updates
      patchV2ProviderAppointmentsByAppointmentId: {
        invalidatesTags: (result, error, { appointmentId }) => [
          { type: 'Appointment', id: appointmentId },
          { type: 'AppointmentsList', id: 'LIST' },
          { type: 'NextAppointment', id: 'NEXT' },
          'Encounter',
        ],
      },

      // Visit lifecycle transitions power Calendar V2's visit-status property row.
      patchV2ProviderAppointmentsByAppointmentIdVisitStatus: {
        invalidatesTags: (result, error, { appointmentId }) => [
          { type: 'Appointment', id: appointmentId },
          { type: 'AppointmentsList', id: 'LIST' },
          { type: 'NextAppointment', id: 'NEXT' },
          'Encounter',
        ],
      },

      postV2ProviderAppointmentsByAppointmentIdCheckIn: {
        invalidatesTags: (result, error, { appointmentId }) => [
          { type: 'Appointment', id: appointmentId },
          { type: 'AppointmentsList', id: 'LIST' },
          { type: 'NextAppointment', id: 'NEXT' },
          'Encounter',
        ],
      },

      postV2ProviderAppointmentsByAppointmentIdCancel: {
        invalidatesTags: (result, error, { appointmentId }) => [
          { type: 'Appointment', id: appointmentId },
          { type: 'AppointmentsList', id: 'LIST' },
          { type: 'NextAppointment', id: 'NEXT' },
          'Encounter',
        ],
      },

      postV2ProviderAppointmentsByAppointmentIdStatusTransitions: {
        invalidatesTags: (result, error, { appointmentId }) => [
          { type: 'Appointment', id: appointmentId },
          { type: 'AppointmentsList', id: 'LIST' },
          { type: 'NextAppointment', id: 'NEXT' },
          'Encounter',
        ],
      },

      postV2ProviderAppointmentsByAppointmentIdReassign: {
        invalidatesTags: (result, error, { appointmentId }) => [
          { type: 'Appointment', id: appointmentId },
          { type: 'AppointmentsList', id: 'LIST' },
          { type: 'NextAppointment', id: 'NEXT' },
          'Encounter',
        ],
      },

      // Enhance appointment deletion
      deleteV2ProviderAppointmentsByAppointmentId: {
        invalidatesTags: (result, error, { appointmentId }) => [
          { type: 'Appointment', id: appointmentId },
          { type: 'AppointmentsList', id: 'LIST' },
          { type: 'NextAppointment', id: 'NEXT' },
        ],
        onQueryStarted: async ({ appointmentId }, { dispatch, queryFulfilled, getState }) => {
          // Optimistically remove the appointment from cache
          const patchCollection: { undo: () => void }[] = [];

          try {
            const state = getState() as RootStateWithQueries;
            const queries = state.api?.queries || {};

            const legacyListQueryKeys = getLegacyAppointmentListQueryKeys(queries);
            const scheduleEventsQueryKeys = getScheduleEventsQueryKeys(queries);

            // Find the appointment's encounter_id before deletion (for cleanup later)
            let appointmentEncounterId: string | undefined;
            for (const queryKey of [...legacyListQueryKeys, ...scheduleEventsQueryKeys]) {
              const appointment = getCachedAppointments(queries[queryKey].data).find(
                (a: any) => a.appointment_id === appointmentId,
              );
              if (appointment?.encounter?.encounter_id) {
                appointmentEncounterId = appointment.encounter.encounter_id;
                break;
              }
            }

            // Remove appointment from all list queries
            for (const queryKey of legacyListQueryKeys) {
              const patch = dispatch(
                enhancedApi.util.updateQueryData(
                  'getV2ProviderAppointments',
                  queries[queryKey].originalArgs,
                  draft => {
                    draft.items = draft.items.filter(a => a.appointment_id !== appointmentId);
                  },
                ),
              );
              patchCollection.push(patch);
            }

            // Remove appointment from the unified schedule-events cache.
            for (const queryKey of scheduleEventsQueryKeys) {
              const patch = dispatch(
                enhancedApi.util.updateQueryData(
                  'getV2ProviderScheduleEvents',
                  queries[queryKey].originalArgs,
                  draft => {
                    draft.appointments = (draft.appointments ?? []).filter(
                      appointment => appointment.appointment_id !== appointmentId,
                    );
                  },
                ),
              );
              patchCollection.push(patch);
            }

            await queryFulfilled;

            // CLEANUP: Clear activeEncounterId if it matches the deleted appointment's encounter
            if (appointmentEncounterId) {
              try {
                const activeEncounterIdRaw = localStorage.getItem('activeEncounterId');
                if (activeEncounterIdRaw) {
                  const activeEncounterId = JSON.parse(activeEncounterIdRaw);
                  // Validate that we got a string (not null, object, etc.)
                  if (typeof activeEncounterId === 'string' && activeEncounterId === appointmentEncounterId) {
                    // Dispatch event to clear encounter context (syncs both localStorage AND React state)
                    dispatchClearActiveEncounter({
                      reason: 'appointment-deleted',
                      encounterId: appointmentEncounterId,
                    });
                  }
                }
              } catch (error) {
                console.error('Failed to check/clear activeEncounterId:', error);
              }
            }
          } catch (error) {
            // Revert optimistic updates on failure
            patchCollection.forEach(patch => patch.undo());
          }
        },
      },

      // Enhance series creation
      postV2ProviderAppointmentsSeries: {
        invalidatesTags: [
          { type: 'AppointmentsList', id: 'LIST' },
          { type: 'NextAppointment', id: 'NEXT' },
        ],
      },

      // Enhance series updates
      patchV2ProviderAppointmentsSeriesBySeriesId: {
        invalidatesTags: (result, error, { seriesId }) => [
          { type: 'AppointmentSeries', id: seriesId },
          { type: 'AppointmentsList', id: 'LIST' },
        ],
      },

      // Enhance series deletion
      deleteV2ProviderAppointmentsSeriesBySeriesId: {
        invalidatesTags: (result, error, { seriesId }) => [
          { type: 'AppointmentSeries', id: seriesId },
          { type: 'AppointmentsList', id: 'LIST' },
          { type: 'NextAppointment', id: 'NEXT' },
        ],
        onQueryStarted: async ({ seriesId }, { queryFulfilled, getState }) => {
          try {
            const state = getState() as RootStateWithQueries;
            const queries = state.api?.queries || {};

            // Find all appointments in this series and collect their encounter IDs
            const encounterIds = new Set<string>();
            const queryKeys = [
              ...getLegacyAppointmentListQueryKeys(queries),
              ...getScheduleEventsQueryKeys(queries),
            ];

            for (const queryKey of queryKeys) {
              const appointments = getCachedAppointments(queries[queryKey].data).filter(
                (a: any) => a.series?.series_id === seriesId,
              );
              appointments.forEach((appointment: any) => {
                if (appointment?.encounter?.encounter_id) {
                  encounterIds.add(appointment.encounter.encounter_id);
                }
              });
            }

            await queryFulfilled;

            // CLEANUP: Clear activeEncounterId if any appointment in the series matches
            if (encounterIds.size > 0) {
              try {
                const activeEncounterIdRaw = localStorage.getItem('activeEncounterId');
                if (activeEncounterIdRaw) {
                  const activeEncounterId = JSON.parse(activeEncounterIdRaw);
                  // Validate that we got a string (not null, object, etc.)
                  if (typeof activeEncounterId === 'string' && encounterIds.has(activeEncounterId)) {
                    // Dispatch event to clear encounter context (syncs both localStorage AND React state)
                    dispatchClearActiveEncounter({
                      reason: 'series-deleted',
                      encounterId: activeEncounterId,
                    });
                  }
                }
              } catch (error) {
                console.error('Failed to check/clear activeEncounterId:', error);
              }
            }
          } catch (error) {
            // Deletion failed, no cleanup needed
          }
        },
      },

      // Enhance encounter creation from appointment
      postV2ProviderEncountersFromAppointmentByAppointmentId: {
        invalidatesTags: (result, error, { appointmentId }) => [
          // Invalidate the specific appointment to reflect the new encounter
          { type: 'Appointment', id: appointmentId },
          // Invalidate the appointments list to update UI
          { type: 'AppointmentsList', id: 'LIST' },
          // Invalidate next appointment to prevent stale collision detection
          { type: 'NextAppointment', id: 'NEXT' },
        ],
      },

      // Enhance appointment rejection
      patchV2ProviderAppointmentsByAppointmentIdReject: {
        invalidatesTags: (result, error, { appointmentId }) => [
          { type: 'Appointment', id: appointmentId },
          { type: 'AppointmentsList', id: 'LIST' },
          { type: 'NextAppointment', id: 'NEXT' },
        ],
      },

      // Enhance mark no-show
      postV2ProviderAppointmentsByAppointmentIdNoShow: {
        invalidatesTags: (result, error, { appointmentId }) => [
          { type: 'Appointment', id: appointmentId },
          { type: 'AppointmentsList', id: 'LIST' },
          { type: 'NextAppointment', id: 'NEXT' },
        ],
      },
    },
  })
  .injectEndpoints({
    endpoints: builder => ({
      // Custom endpoint for bulk operations or complex queries not covered by OpenAPI
      refreshAppointmentData: builder.mutation<void, { forceRefresh?: boolean }>({
        queryFn: async () => ({ data: undefined }),
        invalidatesTags: [
          ...SCHEDULE_EVENTS_LIST_TAGS,
          { type: 'NextAppointment', id: 'NEXT' },
        ],
      }),

      // Manually (re)send an appointment's reminder (SMS + email), powering the
      // gated "Send reminder" action in the appointment detail drawer.
      //
      // Hand-injected rather than consumed from the generated `appointments`
      // slice on purpose: the backend route (GDN-2318) ships in a separate PR,
      // and a generated hook would be `undefined` in any bundle built before
      // that route is live — which crashes on drawer render, since the hook
      // call is unconditional regardless of the feature gate. Defining the
      // endpoint here keeps the hook present in every build, so the action
      // lights up the moment the backend deploys. Sending a reminder doesn't
      // mutate appointment state, so there are no tags to invalidate. Can be
      // swapped to the generated hook during feature-gate cleanup.
      sendAppointmentReminder: builder.mutation<
        { success: boolean; channels_successful?: string[] },
        { appointmentId: string }
      >({
        query: ({ appointmentId }) => ({
          url: `/v2/provider/appointments/${appointmentId}/reminder`,
          method: 'POST',
        }),
      }),
    }),
    overrideExisting: false,
  });

// Export hooks with user-friendly names that match the original API
export const {
  // List appointments (main query) - use generated endpoint
  useGetV2ProviderAppointmentsQuery: useListAppointmentsQuery,
  useGetV2ProviderScheduleEventsQuery: useGetScheduleEventsQuery,

  // Next appointment
  useGetV2ProviderAppointmentsNextQuery: useGetNextAppointmentQuery,

  // Individual appointment operations
  useGetV2ProviderAppointmentsByAppointmentIdQuery: useGetAppointmentQuery,
  usePatchV2ProviderAppointmentsByAppointmentIdMutation: useUpdateAppointmentMutation,
  usePatchV2ProviderAppointmentsByAppointmentIdVisitStatusMutation:
    useUpdateAppointmentVisitStatusMutation,
  usePatchV2ProviderAppointmentsByAppointmentIdRejectMutation: useRejectAppointmentMutation,
  usePostV2ProviderAppointmentsByAppointmentIdCheckInMutation: useCheckInAppointmentMutation,
  usePostV2ProviderAppointmentsByAppointmentIdCancelMutation: useCancelAppointmentMutation,
  usePostV2ProviderAppointmentsByAppointmentIdStatusTransitionsMutation:
    useTransitionAppointmentStatusMutation,
  usePostV2ProviderAppointmentsByAppointmentIdReassignMutation:
    useReassignAppointmentMutation,
  useDeleteV2ProviderAppointmentsByAppointmentIdMutation: useDeleteAppointmentMutation,

  // Create appointment
  usePostV2ProviderAppointmentsMutation: useCreateAppointmentMutation,

  // Series operations
  usePostV2ProviderAppointmentsSeriesMutation: useCreateAppointmentSeriesMutation,
  useGetV2ProviderAppointmentsSeriesBySeriesIdQuery: useGetAppointmentSeriesQuery,
  usePatchV2ProviderAppointmentsSeriesBySeriesIdMutation: useUpdateAppointmentSeriesMutation,
  useDeleteV2ProviderAppointmentsSeriesBySeriesIdMutation: useDeleteAppointmentSeriesMutation,
  useDeleteV2ProviderAppointmentsSeriesBySeriesIdFromAppointmentAndAppointmentIdMutation:
    useDeleteAppointmentSeriesFromAppointmentMutation,

  // Encounter operations
  usePostV2ProviderEncountersFromAppointmentByAppointmentIdMutation:
    useCreateEncounterFromAppointmentMutation,

  // Clinical info operations
  usePostV2ProviderPatientsClinicalInfoMutation: useGetPatientClinicalInfoMutation,
  useGetV2ProviderPatientsByPatientIdClinicalInfoQuery: useGetPatientClinicalInfoQuery,

  // Mark no-show
  usePostV2ProviderAppointmentsByAppointmentIdNoShowMutation: useMarkNoShowMutation,

  // Custom operations
  useRefreshAppointmentDataMutation,
  useSendAppointmentReminderMutation,
} = appointmentsApi;

// Helper function to convert our internal parameters to generated API parameters
export function convertToGeneratedApiParams(
  params: AppointmentQueryParams,
): GetV2ProviderScheduleEventsApiArg {
  return {
    startDate: params.start_date,
    endDate: params.end_date,
    patientId: params.patient_id,
    allProviders: params.all_providers,
    providerIds: params.provider_ids,
    patientIds: params.patient_ids,
    includeCancelled: params.include_cancelled,
  };
}

// Export the API for store configuration
export { appointmentsApi as appointmentsApiSlice };
